import { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useQuery } from "@tanstack/react-query";
import {
  Phone, Shield, Home as HomeIcon, BadgeCheck, Wrench, ChefHat, Bath,
  PaintRoller, Layers, ArrowRight, Star,
} from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import GoogleBadge from "@/components/GoogleBadge";
import { IMAGES } from "@/lib/images";
import api, { fileUrl } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];
const SERVICE_ICONS = [Wrench, ChefHat, Bath, PaintRoller, Layers];
const TRUST_ICONS = [HomeIcon, BadgeCheck, Shield];

function Hero({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const lines = [t("hero.line1"), t("hero.line2"), t("hero.line3")];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden bg-navy">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMAGES.hero}
          alt="Remodeled Miami home"
          initial={{ scale: 1 }}
          animate={{ scale: 1.1 }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "mirror", ease: "linear" }}
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-navy/30" />

      <div className="relative z-10 px-6 md:px-12 max-w-[1400px] mx-auto w-full pb-20 md:pb-28 pt-40">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.7, ease: EASE }}
          className="mb-6 inline-flex items-center gap-3 border border-offwhite/25 bg-navy/30 backdrop-blur-md px-4 py-2"
        >
          <span className="w-2 h-2 bg-orange" />
          <span className="text-offwhite/90 text-xs font-bold uppercase tracking-[0.25em]">{t("hero.kicker")}</span>
        </motion.div>

        <h1 className="font-heading font-black uppercase tracking-tighter text-offwhite text-[13vw] sm:text-7xl lg:text-8xl leading-[0.95]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1, ease: EASE }}
                className={`block ${i === 2 ? "text-orange" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: EASE }}
          className="mt-6 max-w-xl text-offwhite/80 text-base md:text-lg leading-relaxed"
        >
          {t("hero.sub")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to={`${prefix}/estimate`}
            data-testid="hero-estimate-cta"
            className="inline-flex items-center gap-3 bg-orange hover:bg-orange-bright text-white font-bold px-8 py-4 text-sm uppercase tracking-widest transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95"
          >
            {t("hero.cta")} <ArrowRight size={16} />
          </Link>
          <a
            href="tel:17867746692"
            data-testid="hero-call-cta"
            className="inline-flex items-center gap-3 border border-offwhite/30 text-offwhite font-bold px-8 py-4 text-sm uppercase tracking-widest backdrop-blur-md bg-navy/20 transition-[border-color,transform] duration-200 hover:border-orange hover:text-orange hover:scale-105 active:scale-95"
          >
            <Phone size={16} /> {t("hero.call")}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function TrustBadges() {
  const { t } = useTranslation();
  const items = t("trust", { returnObjects: true });
  return (
    <section data-testid="trust-badges" className="relative z-20 -mt-1 bg-navy border-t border-offwhite/10">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto grid grid-cols-1 sm:grid-cols-3">
        {items.map((item, i) => {
          const Icon = TRUST_ICONS[i];
          return (
            <Reveal key={i} delay={i * 0.1} className={`py-10 lg:py-12 px-0 lg:px-8 ${i > 0 ? "lg:border-l border-offwhite/10" : ""}`}>
              <Icon size={28} className="text-orange mb-4" strokeWidth={1.6} />
              <h3 className="font-heading font-bold text-offwhite text-lg tracking-tight">{item.title}</h3>
              <p className="mt-2 text-sm text-offwhite/50 leading-relaxed">{item.desc}</p>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function ServicesPreview({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  const items = t("servicesPreview.items", { returnObjects: true });
  const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"];

  return (
    <section data-testid="services-preview" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <Reveal>
            <Kicker>{t("servicesPreview.kicker")}</Kicker>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">{t("servicesPreview.title")}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to={`${prefix}/services`} data-testid="services-preview-cta" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-navy hover:text-orange transition-colors duration-200">
              {t("servicesPreview.cta")} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {items.map((item, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.08} className={spans[i]}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3, ease: EASE }}
                  className="group h-full bg-white border border-sandline p-8 md:p-10 flex flex-col justify-between min-h-[220px] hover:border-orange/50 hover:shadow-[0_20px_50px_-20px_rgba(11,19,32,0.25)] transition-[border-color,box-shadow] duration-300"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={30} strokeWidth={1.5} className="text-navy group-hover:text-orange transition-colors duration-300" />
                    <span className="font-heading font-black text-4xl text-offwhite-dark group-hover:text-orange/20 transition-colors duration-300">0{i + 1}</span>
                  </div>
                  <div className="mt-8">
                    <h3 className="font-heading font-bold text-xl tracking-tight">{item.title}</h3>
                    <p className="mt-2 text-sm text-slateblue leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WorkPreview({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const y2 = useTransform(scrollYProgress, [0, 1], [-20, 60]);
  const { data: portfolio } = useQuery({
    queryKey: ["portfolio"],
    queryFn: async () => (await api.get("/portfolio")).data,
  });
  const items = (portfolio || []).slice(0, 3);
  const fallbacks = [IMAGES.kitchen, IMAGES.bathroom, IMAGES.hero];
  const ys = [y1, y2, y1];

  return (
    <section ref={ref} data-testid="work-preview" className="py-24 md:py-32 bg-navy text-offwhite overflow-hidden">
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <Reveal>
            <Kicker light>{t("work.kicker")}</Kicker>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-2xl">{t("work.title")}</h2>
            <p className="mt-4 text-offwhite/60 max-w-lg">{t("work.sub")}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to={`${prefix}/portfolio`} data-testid="work-preview-cta" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-offwhite hover:text-orange transition-colors duration-200">
              {t("work.cta")} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <motion.div key={item.id || i} style={{ y: ys[i] }} className={i === 1 ? "md:mt-16" : ""}>
              <Link to={`${prefix}/portfolio`} className="group block" data-testid={`work-preview-item-${i}`}>
                <div className="overflow-hidden">
                  <img
                    src={item.image_path ? fileUrl(item.image_path) : item.image_url || fallbacks[i]}
                    alt={item.title}
                    loading="lazy"
                    className="w-full aspect-[3/4] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-4 flex items-start justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange">{item.category}</span>
                    <h3 className="mt-1 font-heading font-bold text-lg tracking-tight">{item.title}</h3>
                  </div>
                  <ArrowRight size={18} className="mt-1 text-offwhite/40 transition-all duration-300 group-hover:text-orange group-hover:translate-x-1" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  const { data: testimonials } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => (await api.get("/testimonials")).data,
  });
  const items = testimonials || [];
  const featured = items.find((x) => x.featured) || items[0];
  const rest = items.filter((x) => x !== featured).slice(0, 2);

  if (!featured) return null;

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <Reveal>
            <Kicker>{t("testi.kicker")}</Kicker>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">{t("testi.title")}</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <GoogleBadge />
          </Reveal>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7">
            <div className="h-full bg-navy text-offwhite p-10 md:p-14 flex flex-col justify-between">
              <div>
                <div className="flex gap-1 mb-6">
                  {Array.from({ length: featured.rating || 5 }).map((_, i) => (
                    <Star key={i} size={18} className="text-orange" fill="currentColor" />
                  ))}
                </div>
                <blockquote className="font-heading text-2xl md:text-3xl font-medium leading-snug tracking-tight">
                  “{featured.text}”
                </blockquote>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <span className="w-10 h-10 flex items-center justify-center bg-orange text-white font-heading font-bold">{featured.name.charAt(0)}</span>
                <div>
                  <p className="font-bold">{featured.name}</p>
                  <p className="text-sm text-offwhite/50">{featured.location}</p>
                </div>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-5 flex flex-col gap-6">
            {rest.map((item, i) => (
              <Reveal key={item.id} delay={0.1 + i * 0.1} className="flex-1">
                <div className="h-full bg-white border border-sandline p-8 flex flex-col justify-between">
                  <blockquote className="text-slateblue leading-relaxed">“{item.text}”</blockquote>
                  <div className="mt-6">
                    <p className="font-bold text-sm">{item.name}</p>
                    <p className="text-xs text-slateblue">{item.location}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <Link to={`${prefix}/testimonials`} data-testid="testimonials-cta" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-navy hover:text-orange transition-colors duration-200">
                {t("testi.cta")} <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function AreaMarquee() {
  const { t } = useTranslation();
  const areas = t("marquee", { returnObjects: true });
  return (
    <div data-testid="area-marquee" className="bg-offwhite-dark py-6 border-y border-sandline overflow-hidden">
      <Marquee speed={30} gradient={false} pauseOnHover>
        {areas.map((area, i) => (
          <span key={i} className="mx-12 flex items-center gap-12 font-heading font-bold uppercase tracking-[0.3em] text-sm text-navy/50">
            {area} <span className="w-2 h-2 bg-orange inline-block" />
          </span>
        ))}
      </Marquee>
    </div>
  );
}

export function CTASection({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  return (
    <section data-testid="cta-section" className="relative py-28 md:py-40 px-6 md:px-12 bg-navy text-offwhite overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]">
        <img src={IMAGES.worker2} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="relative max-w-[1400px] mx-auto">
        <Reveal>
          <Kicker light>{t("cta.kicker")}</Kicker>
          <h2 className="mt-6 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            {t("cta.title")}
          </h2>
          <p className="mt-6 text-offwhite/60 max-w-xl text-base md:text-lg">{t("cta.sub")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link
              to={`${prefix}/estimate`}
              data-testid="cta-estimate-btn"
              className="inline-flex items-center gap-3 bg-orange hover:bg-orange-bright text-white font-bold px-8 py-4 text-sm uppercase tracking-widest transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95"
            >
              {t("cta.btn")} <ArrowRight size={16} />
            </Link>
            <a href="tel:17867746692" data-testid="cta-call" className="text-offwhite/70 hover:text-orange font-bold text-sm uppercase tracking-widest transition-colors duration-200">
              {t("cta.call")}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home({ lang }) {
  return (
    <>
      <Hero lang={lang} />
      <TrustBadges />
      <ServicesPreview lang={lang} />
      <AreaMarquee />
      <WorkPreview lang={lang} />
      <TestimonialsSection lang={lang} />
      <CTASection lang={lang} />
    </>
  );
}
